import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { Store } from 'redux';
import { History } from 'history';
import { ThemeProvider, createFontFaceCss } from '@openfin/ui-library';
import { Fonts } from '@openfin/ui-library/dist/assets/fonts';
import { StyleSheetManager } from 'styled-components';
import { ConfigurationType } from '@openfin/ui-library/dist/components/system/ThemeProvider/lib/config';

import { WindowProvider } from '../contexts/WindowContext';
import { HistoryContext } from '../contexts/HistoryContext';
import { WebWindow } from '../../model/WebWindow';
import { ServiceStore } from '../../store/ServiceStore';
import { RootState } from '../../store/State';
import { Router, AppRoute } from '../components/Router/Router';

const themeConfig: ConfigurationType = {
  fontLoading: 'import',
  fontCss: createFontFaceCss(Fonts),
};

/**
 * Render a component into a window.
 * @param Component Component to render.
 * @param webWindow Target window.
 * @param store Provider store.
 * @param target DOM element to render to.
 * @param props Props to pass to the Component.
 */
export function renderComponentInWindow<T>(
  Component: (props: T) => React.ReactElement<T> | null,
  webWindow: WebWindow,
  store: ServiceStore,
  target: Element,
  props: T
): void {
  ReactDOM.render(
    <Provider store={(store as unknown) as Store<RootState>}>
      <StyleSheetManager target={webWindow.document.head}>
        <ThemeProvider scheme="dark" config={themeConfig}>
          <WindowProvider value={webWindow.nativeWindow}>
            <Component {...props} />
          </WindowProvider>
        </ThemeProvider>
      </StyleSheetManager>
    </Provider>,
    target
  );
}

/**
 * Appends a component into the body of a window.
 * @param Component Component to render.
 * @param webWindow Target window.
 * @param store Provider store.
 * @param props Props to pass to the Component.
 * @returns the dom element created as append target
 */
export function appendComponentInWindow<T>(
  Component: (props: T) => React.ReactElement<T> | null,
  webWindow: WebWindow,
  store: ServiceStore,
  props: T
): HTMLDivElement {
  const appendTarget = webWindow.document.createElement('div');
  webWindow.document.getElementById('react-app')?.appendChild(appendTarget);
  renderComponentInWindow(Component, webWindow, store, appendTarget, props);
  return appendTarget;
}

/**
 * Render a router and routes into a window.
 * @param routes Routes to display.
 * @param webWindow Target window.
 * @param store Provider store.
 * @param history History state to use for the router.
 * @param target DOM element to render to.
 */
export function renderRouterInWindow(
  routes: AppRoute[],
  webWindow: WebWindow,
  store: ServiceStore,
  history: History,
  target: Element
): void {
  ReactDOM.render(
    <Provider store={(store as unknown) as Store<RootState>}>
      <StyleSheetManager target={webWindow.document.head}>
        <ThemeProvider scheme="dark" config={themeConfig}>
          <HistoryContext.Provider value={history}>
            <WindowProvider value={webWindow.nativeWindow}>
              <Router routes={routes} history={history} />
            </WindowProvider>
          </HistoryContext.Provider>
        </ThemeProvider>
      </StyleSheetManager>
    </Provider>,
    target
  );
}
